import { z } from 'zod';
import { isValidExtensionCompositeId, isValidPublisherName, } from './extension-validators';
import { PolicyKey, policyKeySchema } from './policies';
export var PolicyExceptionAction;
(function (PolicyExceptionAction) {
    PolicyExceptionAction["Allow"] = "allow";
    PolicyExceptionAction["Block"] = "block";
})(PolicyExceptionAction || (PolicyExceptionAction = {}));
export const policyExceptionActionSchema = z.enum(PolicyExceptionAction);
// ---------------------------------------------------------------------------
// Per-policy ruleObject schemas
// ---------------------------------------------------------------------------
/**
 * Checks if a value contains path traversal sequences.
 * Path traversal attempts must be rejected to prevent bypass attacks.
 * Matches actual path traversal patterns: ../, ..\, and .. at end of string.
 * Examples: ../malware, ..\evil, anthropic/.., version1..end (last is valid)
 */
export function hasPathTraversal(value) {
    return /\.\.[\\/]|\.\.$/m.test(value);
}
/**
 * Validates that a glob pattern only uses `*` at the start and/or end.
 * Rejects path traversal characters (../, ..\) to prevent bypass attacks.
 * Allowed: `*`, `foo`, `foo*`, `*foo`, `*foo*`
 * Rejected: `fo*o`, `f*o*o` (interior wildcards), `../malware`, `..\bad` (path traversal)
 */
export function isValidGlobPattern(value) {
    // Reject path traversal sequences using shared check
    if (hasPathTraversal(value))
        return false;
    if (/\*{2,}/.test(value))
        return false;
    const inner = value.replace(/^\*/, '').replace(/\*$/, '');
    return !inner.includes('*');
}
const globStringSchema = z.string().refine(isValidGlobPattern, {
    message: 'Wildcards (*) are only allowed at the start and/or end of the value. Valid examples: my-extension, my-org*, *my-publisher, *partial*',
});
export const extensionsManagementRuleSchema = z
    .object({
    name: globStringSchema.optional(),
    publisher: globStringSchema.optional(),
    version: z.string().optional(),
})
    .refine(obj => obj.name || obj.publisher, {
    message: 'At least name or publisher must be provided',
})
    .superRefine((obj, ctx) => {
    if (obj.name && !obj.name.includes('*')) {
        if (!isValidExtensionCompositeId(obj.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Name must be publisher.extension (lowercase alphanumeric and hyphens, e.g. ms.python) or a wildcard pattern',
                path: ['name'],
            });
        }
    }
    if (obj.publisher && !obj.publisher.includes('*')) {
        if (!isValidPublisherName(obj.publisher)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid publisher name (lowercase letters, digits, hyphens; optional dot-separated segments; 2-30 characters) or use a wildcard pattern',
                path: ['publisher'],
            });
        }
    }
});
export var McpServerSourceType;
(function (McpServerSourceType) {
    McpServerSourceType["Local"] = "local";
    McpServerSourceType["Remote"] = "remote";
    McpServerSourceType["Any"] = "any";
})(McpServerSourceType || (McpServerSourceType = {}));
export const mcpServerSourceTypeSchema = z.enum(McpServerSourceType);
export const mcpServerSourcesRuleSchema = z
    .object({
    name: globStringSchema.optional(),
    publisher: globStringSchema.optional(),
    version: z.string().optional(),
    type: mcpServerSourceTypeSchema.optional(),
})
    .refine(obj => obj.name || obj.publisher, {
    message: 'At least name or publisher must be provided',
});
export const ruleObjectSchemasByPolicy = {
    [PolicyKey.ExtensionsManagement]: extensionsManagementRuleSchema,
    [PolicyKey.McpServerSources]: mcpServerSourcesRuleSchema,
};
// ---------------------------------------------------------------------------
// Generic ruleObject schema (used in create/update body for Fastify schema)
// ---------------------------------------------------------------------------
export const ruleObjectSchema = z
    .record(z.string(), z.unknown())
    .refine(obj => Object.values(obj).some(v => typeof v === 'string' && v.trim().length > 0), { message: 'At least one field in ruleObject must be a non-empty string' });
export const policyExceptionSchema = z.object({
    id: z.uuid(),
    organizationId: z.string().nullable(),
    userId: z.uuid().nullable(),
    // KIR-3800: set (with org/user null) for a security-group-scoped exception, so
    // a caller can tell group exceptions apart from org/user ones. Nullable +
    // additive — org/user exceptions serialize it as null.
    securityGroupId: z.string().nullable(),
    policyKey: policyKeySchema,
    action: policyExceptionActionSchema,
    ruleObject: z.record(z.string(), z.unknown()),
    createdBy: z.uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedBy: z.uuid().nullable(),
});
export const createPolicyExceptionSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: ruleObjectSchema,
});
export const updatePolicyExceptionSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: ruleObjectSchema,
});
// ---------------------------------------------------------------------------
// Add-from-alert (KIR-3558): one-click add of an alert's entity to a list
// ---------------------------------------------------------------------------
/**
 * The only policies the add-from-alert action can target — the two Allow/Block
 * list policies. Single source of truth shared by the route param schema
 * (rejects any other key with a 400 before the handler runs) and the backend
 * handler's defensive guard, so the two can never drift.
 */
export const ADD_FROM_ALERT_SUPPORTED_POLICIES = [
    PolicyKey.ExtensionsManagement,
    PolicyKey.McpServerSources,
];
/**
 * Route-param schema for the add-from-alert endpoints. Unlike `policyKeySchema`
 * (which accepts every policy key), this restricts to the Allow/Block list
 * policies so an unsupported-but-valid key is a client error (400 at schema
 * validation), never a server 500.
 */
export const fromAlertPolicyKeySchema = z.enum(ADD_FROM_ALERT_SUPPORTED_POLICIES);
/**
 * Request body for `POST .../exceptions/from-alert`. Identical shape to a
 * create, but the handler applies add-only prechecks (already-on-list /
 * covered-by-broad-rule) instead of surfacing duplicates as errors.
 */
export const addPolicyExceptionFromAlertSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: ruleObjectSchema,
});
/**
 * The existing rule that already covers the entity, returned on the two "stop"
 * outcomes so the UI can show exactly why nothing was added.
 */
export const coveringPolicyRuleSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: z.record(z.string(), z.unknown()),
});
export var AddFromAlertStatus;
(function (AddFromAlertStatus) {
    AddFromAlertStatus["Added"] = "added";
    AddFromAlertStatus["AlreadyOnList"] = "already_on_list";
    AddFromAlertStatus["CoveredByBroadRule"] = "covered_by_broad_rule";
})(AddFromAlertStatus || (AddFromAlertStatus = {}));
/**
 * Result of an add-from-alert action, discriminated on `status`. All three are
 * successful (2xx) outcomes — the frontend branches on `status`, never on the
 * HTTP code, and reserves non-2xx for real failures.
 * - `added` (201): the exact entry was created.
 * - `already_on_list` (200): the exact entry already exists (no change).
 * - `covered_by_broad_rule` (200): a broader rule already covers it (no change).
 */
export const addFromAlertResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal(AddFromAlertStatus.Added),
        exception: policyExceptionSchema,
    }),
    z.object({
        status: z.literal(AddFromAlertStatus.AlreadyOnList),
        coveringRule: coveringPolicyRuleSchema,
    }),
    z.object({
        status: z.literal(AddFromAlertStatus.CoveredByBroadRule),
        coveringRule: coveringPolicyRuleSchema,
    }),
]);
export const listPolicyExceptionsQuerySchema = z.object({
    action: policyExceptionActionSchema.optional(),
});
export const policyExceptionListSchema = z.object({
    exceptions: z.array(policyExceptionSchema),
});
//# sourceMappingURL=policy-exceptions.js.map